import { ChangeEvent, HTMLAttributes, useMemo } from 'react'

import Button from '@/components/base/button'
import Spinner from '@/components/base/spinner'
import SVGIcon from '@/components/base/svg-icon'
import Typography from '@/components/base/typography'

import { cn } from '@/lib/utils'

export type ButtonSelectOption = {
  label: string
  value: string
}

export type ButtonSelectProps = HTMLAttributes<HTMLDivElement> &
  Partial<{
    value: string
    fetching: boolean
    placeholder: string
    options: ButtonSelectOption[]
    // eslint-disable-next-line no-unused-vars
    onFilterChange: (event: ChangeEvent<HTMLSelectElement>) => void
  }>

const ButtonSelect = ({
  value,
  placeholder,
  options,
  fetching,
  onFilterChange,
  className,
  ...props
}: ButtonSelectProps) => {
  const selected = useMemo<ButtonSelectOption>(
    () => options?.find((option) => option.value.toString() === value)!,
    [value, options]
  )

  return (
    <div className={cn('relative', className)} {...props}>
      <Button
        isLiquid
        full
        variant="secondary"
        className="w-full justify-between [&_svg[data-background]]:stroke-border-menu-light"
        endIcon={
          <SVGIcon
            symbol="chevron-down-icon"
            className={cn([
              'size-[1rem] transform transition-transform',
              {
                'opacity-0 md:opacity-100': fetching
              }
              // { 'rotate-180': isDropdownOpen }
            ])}
          />
        }
      >
        <Typography variant="regular">
          {selected?.label || placeholder}
        </Typography>
      </Button>

      <select
        value={value}
        className="absolute left-0 top-0 z-30 h-full w-full cursor-pointer appearance-none px-spacing-2xs opacity-0"
        onChange={onFilterChange}
      >
        <option value="">{`--${placeholder}--`}</option>
        {options?.map((option, index) => (
          <option key={index} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>

      {fetching ? (
        <div className="absolute right-[1.3rem] top-1/2 -translate-y-1/2 md:hidden">
          <Spinner variant="small" />
        </div>
      ) : null}
    </div>
  )
}

export default ButtonSelect
